"use client";

import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Search } from "lucide-react";
import { NAV_ITEMS } from "@/lib/mock-data";

/** Global Shell primitive: global search / command entry (⌘K style palette). */
export function CommandSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const results = NAV_ITEMS.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-control border border-border-subtle bg-surface-primary text-text-secondary shadow-[var(--shadow-subtle)] hover:bg-surface-secondary sm:w-56 sm:justify-start sm:gap-2 sm:px-3"
          aria-label="Open global search"
        >
          <Search aria-hidden="true" size={16} />
          <span className="hidden text-xs sm:inline">Search or jump to…</span>
          <kbd className="ms-auto hidden rounded border border-border-subtle px-1.5 py-0.5 text-[10px] text-text-secondary sm:inline">
            ⌘K
          </kbd>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay fixed inset-0 z-40 bg-[rgba(23,25,21,0.4)]" />
        <Dialog.Content className="fixed start-1/2 top-24 z-50 w-[min(92vw,540px)] -translate-x-1/2 rtl:translate-x-1/2 rounded-card border border-border-subtle bg-surface-elevated p-2 shadow-[var(--shadow-lifted)]">
          <Dialog.Title className="sr-only">Global search</Dialog.Title>
          <Dialog.Description className="sr-only">Search screens and commands across Highland Host.</Dialog.Description>
          <div className="flex items-center gap-2 border-b border-border-subtle px-2 pb-2">
            <Search aria-hidden="true" size={16} className="text-text-secondary" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search screens, guests, reservations…"
              className="w-full bg-transparent py-2 text-sm text-text-primary outline-none placeholder:text-text-secondary"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
            {results.length === 0 ? (
              <li className="px-3 py-6 text-center text-xs text-text-secondary">No matches for &ldquo;{query}&rdquo;.</li>
            ) : (
              results.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex w-full items-center justify-between rounded-control px-3 py-2 text-start text-sm text-text-primary hover:bg-surface-secondary"
                  >
                    <span>{item.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-text-secondary">{item.section}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
